<template>
    <div>
        <b-alert v-model="isLocked" variant="info" dismissible>
            {{ this.lockMessage }}
        </b-alert>
        <b-alert :show="isRemoteManaged" variant="info">
            This follow-up was sent to a partner pharmacy and is managed there. It is shown here for
            tracking only; its status updates automatically when the partner pharmacy resolves it.
        </b-alert>
        <b-row>
            <b-col cols="6">
                <!-- Show Document -->
                <ImageComponent style="height:835px; border: 1px solid black;" :imageID="imageID">
                </ImageComponent>
                <b-button v-if="!!imageID && !isRemoteManaged && !isAutoCancelled" v-b-modal="resendFaxModalId" class="mt-2">
                    Resend Fax
                </b-button>
                <b-modal :id="resendFaxModalId" title="Resend Fax" ok-title="Send Fax"
                         @ok.prevent="resendFax" @hidden="clearResendFaxForm">
                    <p class="text-muted small mb-2">Optionally add a note. It will appear on a cover page prepended to the document.</p>
                    <b-form-group label="Note on Fax:" :description="`${resendFaxNote.length}/2000 characters`">
                        <b-textarea v-model="resendFaxNote" maxlength="2000" rows="4" />
                    </b-form-group>
                </b-modal>
            </b-col>
            <b-col cols="6">
                <h3>
                    {{ value.requestName }}
                    <b-badge :variant="statusVariant" class="align-middle">{{ statusLabel }}</b-badge>
                </h3>
                <!-- Doctor information -->
                <div v-if="!!value.storeID">
                    <router-link target="_blank" class="ml-2" v-if="prescription" :to="prescriptionDetailsRoute">
                        <span>Go To Rx...</span>
                    </router-link>
                </div>
                <hr>
                <div>
                    <b>TO DOCTOR: </b><span>{{ doctor.displayNamesForPerson() }}</span><br>
                    <span>{{ doctor.fullAddress }}</span><br>
                    <b>PHONE: </b><span>{{ doctor.phoneNumber | formatPhone }}</span><br>
                    <b>FAX: </b><span>{{ doctor.faxNumber }}</span>
                    <b-check v-if="doctor.altFaxNumber" v-model="useAltFax">
                        <b>ALT FAX: </b> <span>{{ doctor.altFaxNumber }}</span>
                    </b-check>
                </div>
                <hr>

                <!-- Patient Information -->
                <div>
                    <b>PATIENT: </b><span>{{ patient.displayNamesForPerson() }}</span><br>
                    <span>{{ patient.fullAddress }}</span><br>
                    <b>PHONE: </b><span>{{ patient.phoneNumber | formatPhone }}</span><br>
                    <b>PATIENT DOB: </b><span>{{ patient.dateOfBirth | formatLocalDate('L') }}</span>
                </div>
                <hr>

                <!-- Rx Information -->
                <PrescriptionDetailsSummary v-if="prescription && value.type != requestTypeEnum.NewRxRequest"
                    v-model="prescription"></PrescriptionDetailsSummary>
                <div v-if="drug">
                    <b>PRESCRIBED: </b><span>{{ drug.productNameLong?.length > 0 ? drug.productNameLong :
                        drug.productNameShort }}</span><br>
                    <b>NDC: </b><span>{{ drug.ndc }}</span>
                </div>
                <hr>

                <!-- Who last acted on this case. A case the auto-close worker resolved shows as a
                     system action rather than as its internal service name. -->
                <div class="text-muted small">
                    <b>LAST ACTIVITY: </b><span>{{ lastActivityDescription }}</span>
                </div>
                <hr>

                <!-- Notes -->
                <div>
                    <b>Notes: </b>
                    <NotesList ref="notes-list" :paging="false" :entityId="+value.id" :entityType="entityType" />
                </div>

                <div class="form-group" v-if="!isLocked">
                    <NotesForm :entityId="+value.id" :entityType="entityType" @saved="refreshNotesList"
                        :templates="noteTemplates" />
                </div>
            </b-col>
        </b-row>

        <!-- Action Buttons. Withheld once the case is closed: every one of them resolves the case, and
             re-resolving a closed one re-fires the prescriber fax and the patient call, or returns a
             canceled case to the queue. -->
        <b-row v-if="isOpen && !isLocked && !isSending && !isRemoteManaged">
            <b-col>
                <b-button variant="warning" @click="confirmDialog(prescriberFollowUpStatusEnum.InProgress)"
                    v-b-tooltip.hover title="Click if no response.">Hold</b-button>
                <b-button variant="danger" @click="confirmDialog(prescriberFollowUpStatusEnum.NotResponded)"
                    v-b-tooltip.hover
                    title="Click to close case if it won't be possible to contact the doctor.">
                    No Success
                </b-button>
                <b-button variant="danger" @click="confirmDialog(prescriberFollowUpStatusEnum.Denied)"
                    v-b-tooltip.hover
                    title="Click if the prescriber denied the request. The prescriber will not be contacted.">
                    Denied
                </b-button>
                <b-button variant="secondary" @click="confirmDialog(prescriberFollowUpStatusEnum.Canceled)"
                    v-b-tooltip.hover
                    title="Click to close a case opened by mistake. No one is contacted.">
                    Cancel
                </b-button>
                <b-button variant="success" @click="confirmDialog(prescriberFollowUpStatusEnum.Responded)"
                    v-b-tooltip.hover
                    title="Click if doctor responded and all information was gathered.">Complete</b-button>
            </b-col>
        </b-row>

        <!-- Reopen: the only way back out of a cancellation -->
        <b-row v-if="!isLocked && !isSending && !isRemoteManaged && isAutoCancelled">
            <b-col>
                <b-button variant="warning" @click="confirmDialog(prescriberFollowUpStatusEnum.Created)"
                    v-b-tooltip.hover
                    title="Click if this case was cancelled in error and the prescriber still needs to respond.">
                    Reopen
                </b-button>
            </b-col>
        </b-row>

        <!-- Denied closes without ever contacting the prescriber; reaching the patient is opt-in. -->
        <b-modal :id="denyModalId" title="Confirm" size="sm" ok-title="YES" cancel-title="NO" ok-variant="info"
                 footer-class="p-2" centered @ok="submit(prescriberFollowUpStatusEnum.Denied, notifyPatientOnDeny)"
                 @hidden="notifyPatientOnDeny = false">
            <p>
                The case will be closed because the prescriber denied the request. The prescriber will not
                be contacted. Are you sure?
            </p>
            <b-check v-model="notifyPatientOnDeny">Notify the patient</b-check>
        </b-modal>

        <b-row v-if="isSending">
            <b-spinner label="Spinning" class="mr-2" />
            <strong>Sending...</strong>
        </b-row>

        <PatientRxHistoryModalComponent ref="doc_form_patient_rx_history_modal" :patient="patient" />

        <patient-notes-modal-component v-if="patient && patient.id" :entity-id="patient.id" />
        <doctor-notes-modal-component v-if="doctor && doctor.id" :entity-id="doctor.id" />
        <drug-notes-modal-component v-if="drug && drug.drugId" :entity-id="drug.drugId" :drug-source="drug.source" />
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import axios, { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import { LockHandler, Lock } from '@/mixins/LockHandler';
import { PrescriberFollowUpCase, FaxType, PrescriberFollowUpStatus, displayFollowUpActor, isOpenFollowUpStatus } from '@/models/PrescriberFollowUpCase';
import { Prescriber } from '@/models/Prescriber';
import { Patient } from '@/models/Patient';
import { PatientDocument } from '@/models/PatientDocument';
import { Prescription } from '@/models/Prescription';
import { Drug } from '@/models/Drug/Drug';
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct';
import { NonGSDDDrug } from '@/models/Drug/NonGSDDDrug';
import { CompoundDrug } from '@/models/Drug/CompoundDrug';
import { Note } from '@/models/Note';
import TextUtils from '@/util/TextUtils';

import ImageComponent from '@/components/Prescription/ImageComponent.vue';
import PrescriptionDetailsSummary from '@/components/Prescription/PrescriptionDetailsSummary.vue';
import NotesList from '@/components/Note/NotesList.vue';
import NotesForm from '@/components/Note/NotesForm.vue';
import PatientRxHistoryModalComponent from '@/components/Patient/PatientRxHistoryModalComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { EntityType } from "@/models/EntityType";
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";

// Per-instance modal id. b-modal routes show/hide events by id, so when this
// component is mounted more than once on a page a static id would cause every
// copy to open in response to a single trigger.
let modalInstanceCounter = 0;

/**
 * Confirm wording per status the form can move a case to. Hold (InProgress) is absent because its
 * wording depends on the resolved next-touch configuration and is built asynchronously; Denied is
 * absent because it uses its own modal rather than a message box.
 */
const CONFIRM_MESSAGES: Partial<Record<PrescriberFollowUpStatus, string>> = {
    [PrescriberFollowUpStatus.Created]: "The case will be reopened and return to the follow-up queue. Its aging clock resets to now, so it won't be closed again automatically until a full follow-up cycle passes.",
    [PrescriberFollowUpStatus.NotResponded]: "The case will be closed because the doctor won't be able to be contacted",
    [PrescriberFollowUpStatus.Responded]: "The case will be closed because all the information needed was gathered",
    [PrescriberFollowUpStatus.Canceled]: "The case will be closed with no communication to the patient or the prescriber",
};

/** What the next touch would be if the case moved to a given status now. Returned only by the
 *  next-touch endpoint — the queue payload carries neither field in this shape. */
interface NextTouchProjection {
    nextTouchDays?: number;
    nextTouchDate?: string;
}

@Component({
    name: "DoctorFollowUpCaseForm",
    components: {
        DrugNotesModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        ImageComponent,
        PrescriptionDetailsSummary,
        NotesList, NotesForm,
        PatientRxHistoryModalComponent,
    },
})
export default class DoctorFollowUpCaseForm extends Mixins(LockHandler) {
    @Prop() private value!: PrescriberFollowUpCase;
    private lock: Lock | null = null;
    private isLocked = false;
    private lockMessage = '';
    private isSending = false;
    private doctor: Prescriber = new Prescriber();
    private patient: Patient = new Patient();
    private followUpCase: PrescriberFollowUpCase = new PrescriberFollowUpCase();

    private prescription: Prescription | null = null;
    /** Any of the three drug sources, so this is the shared base rather than the GSDD type. */
    private drug: DrugProduct | null = null;

    private imageID: number = 0;
    private entityType = EntityType.PrescriberFollowUpCase;

    protected notes: Array<Note> = Array<Note>();
    protected prescriberFollowUpStatusEnum = PrescriberFollowUpStatus;
    protected requestTypeEnum = FaxType;

    protected useAltFax = false;
    protected resendFaxNote = '';
    private readonly modalUid = ++modalInstanceCounter;
    protected resendFaxModalId = `resend-fax-modal-${this.modalUid}`;
    protected denyModalId = `deny-follow-up-modal-${this.modalUid}`;
    protected notifyPatientOnDeny = false;

    /** A case sent to a partner pharmacy is a local tracking mirror only — resolution happens remotely. */
    protected get isRemoteManaged(): boolean {
        return !!this.value?.remoteInstanceCaseId;
    }

    protected get currentStatus(): PrescriberFollowUpStatus | undefined {
        return this.followUpCase?.status ?? this.value?.status;
    }

    /** Reopen is the only way back out of a cancellation, so it is offered for that status alone. */
    protected get isAutoCancelled(): boolean {
        return this.currentStatus === PrescriberFollowUpStatus.Canceled;
    }

    protected get statusLabel(): string {
        return this.currentStatus === undefined ? '' : TextUtils.camelCaseToNormal(PrescriberFollowUpStatus[this.currentStatus]);
    }

    protected get statusVariant(): string {
        switch (this.currentStatus) {
            case PrescriberFollowUpStatus.Responded: return 'success';
            case PrescriberFollowUpStatus.NotResponded: return 'danger';
            case PrescriberFollowUpStatus.Denied: return 'danger';
            case PrescriberFollowUpStatus.Canceled: return 'secondary';
            default: return 'primary';
        }
    }

    /**
     * Whether the case can still be acted on. The host may open this form from a history list that
     * includes closed cases, and resolving one again would re-fire the prescriber fax and the patient
     * call, or put a canceled case back in the queue.
     */
    protected get isOpen(): boolean {
        return isOpenFollowUpStatus(this.value?.status);
    }

    /** Who last acted on the case and when, so a system-initiated close is distinguishable from a staff one. */
    protected get lastActivityDescription(): string {
        const actor = displayFollowUpActor(this.value?.updatedBy);
        if (!this.value?.updated) return actor;

        return `${actor} on ${moment.utc(this.value.updated).local().format('l LT')}`;
    }

    loadInfo() {
        if (!this.value.id) return;

        this.isLocked = false;
        // Hosts swap v-model while this form stays mounted, so reap the previous case's lock.
        this.setExclusiveLock(`/prescriber/follow-up-case/${this.value.id}/lock`, 60000)
            .then(res => this.lock = res)
            .catch(err => {
                console.log(`Added lock -- error == ${err}`);
                if (err.response && err.response.status == 418) {
                    // patient locked
                    this.showLockedMessage(
                        EntityType.PrescriberFollowUpCase,
                        this.value?.id.toString(),
                        this.value?.id.toString(),
                        err.response.data
                    );
                }
            })
            .finally(() => this.fetchData());
    }

    created() {
        this.loadInfo();
    }

    @Watch('value')
    onValueChange(newValue: PrescriberFollowUpCase, oldValue: PrescriberFollowUpCase) {
        if (newValue.id && oldValue.id && newValue.id === oldValue.id) return;
        this.loadInfo();
    }

    async fetchData() {
        this.followUpCase = new PrescriberFollowUpCase(this.value);

        let resDoctor = await this.$http.get<Prescriber>(`/Prescriber/${this.value.prescriberId}`)
            .catch(err => this.catchError(err, "Error loading doctor information."));

        if (resDoctor?.data.id) this.doctor = new Prescriber((resDoctor as AxiosResponse).data);

        let resPatient = await this.$http.get<Patient>(`/Patient/${this.value.patientId}`)
            .catch(err => this.catchError(err, "Error loading patient information."));

        if (resPatient?.data.id) this.patient = new Patient(resPatient.data);

        let resDocs = await this.$http.get<Array<PatientDocument>>(`/Patient/${this.value.patientId}/get-documents`)
            .catch(err => this.catchError(err, "Error loading patient documents."));

        if (resDocs?.data.length)
            this.imageID = resDocs.data.find((doc: PatientDocument) => doc.id == this.value.documentId)?.imageId ?? 0;

        if (this.value.packageID) {
            let resDrug = await this.$http.get<Drug>(`/drug/${this.value.packageID}`, { params: { drugSource: this.value.drugSource } })
                .catch(err => this.catchError(err, "Error loading drug information."));

            // drugId, not packageID: packageID is GSDD's key alone, so gating on it silently dropped
            // every non-GSDD product and compound and left the case with no drug shown. drugId is the
            // identifier IDrugProduct carries for all three sources, and the endpoint guarantees it.
            //
            // The concrete type has to follow the source too. A non-GSDD payload carries `status` as a
            // numeric DrugStatusEnum, which overwrites the DrugStatus object Drug's constructor builds
            // and then throws when it assigns through it — so admitting those responses without
            // choosing the matching type would trade a missing drug for a broken fetch.
            if (resDrug?.data?.drugId) {
                const product = resDrug.data;
                switch (product.source) {
                    case DrugSource.NonGsddProduct:
                        this.drug = new NonGSDDDrug(product.drugId, product);
                        break;
                    case DrugSource.CompoundProduct:
                        this.drug = new CompoundDrug(product.drugId, product);
                        break;
                    default:
                        this.drug = new Drug(product.drugId, product);
                        break;
                }
            }
        }

        if (this.value.rxNumber) {
            const rxID = Prescription.createRxId(this.value.storeID, this.value.rxNumber, this.value.rfNumber);
            let resRx = await this.$http.get<Prescription>(`/prescription/${rxID}`)
                .catch(err => this.catchError(err, "Error loading prescription information."));

            if (resRx?.data.rxNumber)
                this.prescription = new Prescription(resRx.data);
        }
    }

    catchError(err: any, errorMsg: string): AxiosResponse {
        console.error(errorMsg, { err, response: err?.response });

        let displayMessage = errorMsg;
        if (err?.response?.data?.error) {
            displayMessage = err.response.data.error;
        } else if (err?.response?.data) {
            displayMessage = typeof err.response.data === 'string' ? err.response.data : errorMsg;
        }

        this.$notification(NotificationOptions.error(displayMessage));
        return {} as AxiosResponse;
    }

    get prescriptionDetailsRoute() {
        return {
            name: 'PrescriptionDetails'
            , params: {
                storeID: this.prescription?.storeID,
                rxNumber: this.prescription?.rxNumber,
                rfNumber: (this.prescription?.rfNumber || 0),
            },
        };
    }

    submit(statusToSubmit: PrescriberFollowUpStatus, notifyPatient = false) {
        this.followUpCase.status = statusToSubmit;

        this.isSending = true;
        this.$http.post<PrescriberFollowUpCase>('/Prescriber/follow-up-queue', this.followUpCase, { params: { useAltFax: this.useAltFax, notifyPatient } })
            .then(res => {
                if (res?.data.id) {
                    this.followUpCase = new PrescriberFollowUpCase(res.data);
                    this.$notification(NotificationOptions.notificationOptionsPreset(`Follow-up passed to status: ${this.followUpCase.statusName}`, NotificationOptions.NotificationTypes.success));
                    this.$emit('submitted', this.followUpCase);
                } else this.$notification(NotificationOptions.errorSaveNotificationPreset(`Follow-up case`));
            })
            .catch(err => {
                let errorMessage = "Error submitting follow up case";
                if (err?.response?.data?.error) {
                    errorMessage = err.response.data.error;
                } else if (err?.response?.data && typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                }

                console.error("Error submitting follow up case", { err, response: err?.response });
                this.$notification(NotificationOptions.error(errorMessage));
            })
            .finally(() => this.isSending = false);
    }

    /**
     * How long a hold actually lasts, resolved for this case's program and store rather than assumed.
     * Falls back to wording without a duration if the projection can't be loaded, so the dialog never
     * states a figure it hasn't confirmed.
     */
    async holdMessage(): Promise<string> {
        const projection = await this.fetchNextTouchProjection(PrescriberFollowUpStatus.InProgress);
        const days = projection?.nextTouchDays;
        if (!days) return "The case will be hidden from the queue to try again another day";

        const duration = `${days} business day${days === 1 ? '' : 's'}`;
        const returning = projection?.nextTouchDate
            ? `, returning on ${moment.utc(projection.nextTouchDate).local().format('ddd, MMM D [at] LT')}`
            : '';

        return `The case will be hidden from the queue for ${duration}${returning}`;
    }

    async fetchNextTouchProjection(status: PrescriberFollowUpStatus): Promise<NextTouchProjection | null> {
        if (!this.value?.id) return null;

        const res = await this.$http
            .get<NextTouchProjection>(`/Prescriber/follow-up-case/${this.value.id}/next-touch`, { params: { status } })
            .catch(err => {
                console.error("Error loading next touch projection.", { err, response: err?.response });
                return null;
            });

        return res?.data ?? null;
    }

    async confirmDialog(status: PrescriberFollowUpStatus) {
        // Denied carries an opt-in for patient contact, so it needs a real modal rather than a message box.
        if (status == PrescriberFollowUpStatus.Denied) {
            this.$bvModal.show(this.denyModalId);
            return;
        }

        // Hold is the one status whose wording depends on configuration, so it resolves asynchronously.
        const message = status == PrescriberFollowUpStatus.InProgress
            ? await this.holdMessage()
            : CONFIRM_MESSAGES[status] ?? 'The case will be closed';


        this.$bvModal.msgBoxConfirm(`${message}. Are you sure?`, {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'info',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
        })
            .then(value => {
                if (!value) return;
                this.submit(status);
            })
            .catch(err => {
                console.error("Error caught on Clear button.", err);
            });
    }

    mounted() {
        document.addEventListener("keydown", this.preventDefaultForHotkeys);
        document.addEventListener("keyup", this.hotKeysListener);
        this.fetchNoteTemplates();
    }

    beforeDestroy() {
        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
        document.removeEventListener("keyup", this.hotKeysListener);
    }

    preventDefaultForHotkeys(e: any) {
        if (e.code === "F5") e.preventDefault();
    }

    hotKeysListener(e: any) {
        if (e.code === 'F5' && this.patient?.id) {
            this.openRxHistory();
        }
    }

    openRxHistory() {
        const modal = this.$refs["doc_form_patient_rx_history_modal"] as PatientRxHistoryModalComponent;
        if (modal) modal?.openRxHistory();
        else console.error("Reference dont exist for the component", modal);
    }

    async resendFax() {
        try {
            await axios.post(
                `/api/Prescriber/follow-up-case/${this.followUpCase.id}/resend-fax`,
                { note: this.resendFaxNote },
                { params: { useAltFax: this.useAltFax } });
            this.$notification(NotificationOptions.notificationOptionsPreset('Fax resent successfully', NotificationOptions.NotificationTypes.success));
            this.refreshNotesList();
            // Close on success; @hidden -> clearResendFaxForm will wipe the note.
            // On failure we deliberately don't hide so the typed note isn't lost.
            this.$bvModal.hide(this.resendFaxModalId);
        } catch (e) {
            this.$notification(NotificationOptions.error(e));
        }
    }

    clearResendFaxForm() {
        this.resendFaxNote = '';
    }

    @Ref("notes-list") protected notesList!: NotesList;

    refreshNotesList() {
        this.notesList.loadNotes();
    }

    protected noteTemplates: string[] = [];

    async fetchNoteTemplates() {
        try {
            const resp = await axios.get<string[]>("/api/Configuration/PrescriberFollowUpNoteTemplates");
            this.noteTemplates = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

}

</script>

<style scoped></style>
